"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, BarChart3, TrendingUp, PieChart, Database, BookOpen, Telescope, Calculator } from 'lucide-react';
import { AboutSidebar } from '@/components/about/sidebar';

export default function StatisticalPage() {
  return (
    <div className="min-h-screen bg-slate-950">
      <div className="flex h-screen">
        {/* Sidebar */}
        <AboutSidebar activeSection="astrology" />

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-4 py-8">
            {/* Current Section Indicator */}
            <div className="mb-4 p-3 bg-cyan-500/10 border border-cyan-500/20 rounded-lg">
              <div className="flex items-center gap-2 text-cyan-300">
                <Telescope className="h-4 w-4" />
                <span className="text-sm font-medium">🔭 Astroloji</span>
                <span className="text-cyan-500">/</span>
                <span className="text-sm">Gelişmiş Sistemler</span>
                <span className="text-cyan-500">/</span>
                <span className="text-sm">İstatistiksel Astroloji</span>
              </div>
            </div>

            {/* Breadcrumb */}
            <div className="mb-6">
              <Link
                href="/about"
                className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                AXTROLOG Nedir
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <Link
                href="/about/astrology"
                className="text-slate-400 hover:text-slate-200 transition-colors"
              >
                Astroloji
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <Link
                href="/about/astrology/advanced-systems"
                className="text-slate-400 hover:text-slate-200 transition-colors"
              >
                Gelişmiş Sistemler
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <span className="text-slate-300">İstatistiksel Astroloji</span>
            </div>

            {/* Header */}
            <div className="bg-gradient-to-r from-cyan-900/20 to-blue-900/20 rounded-xl p-8 mb-8 border border-cyan-500/20">
              <h1 className="text-4xl font-bold text-slate-100 mb-4 flex items-center gap-3">
                <BarChart3 className="h-10 w-10 text-cyan-400" />
                İstatistiksel Astroloji
              </h1>
              <p className="text-lg text-slate-200 leading-relaxed">
                Büyük veri setleri ve matematiksel analiz yöntemlerini kullanarak astrolojik
                korelasyonları araştıran bilimsel yaklaşım. Michel Gauquelin'in öncülük ettiği
                bu alan, astrolojik iddiaları istatistiksel yöntemlerle test eder ve
                anlamlı korelasyonları ortaya çıkarmaya odaklanır.
              </p>
            </div>

            {/* Basic Concept */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Temel Kavramlar</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
                      <Database className="h-5 w-5 text-cyan-400" />
                      Veri Odaklı Yaklaşım
                    </h3>
                    <div className="space-y-3">
                      <p className="text-slate-300">
                        Binlerce hatta milyonlarca doğum verisinin istatistiksel
                        analizi ile astrolojik korelasyonları araştırır.
                      </p>
                      <div className="bg-cyan-900/20 border border-cyan-700/50 rounded-lg p-4">
                        <h4 className="font-semibold text-cyan-200 mb-2">Metodoloji:</h4>
                        <div className="space-y-1 text-sm text-slate-300">
                          <div>• Büyük örneklem grupları (n&gt;1000)</div>
                          <div>• Kontrol grupları kullanımı</div>
                          <div>• Chi-square ve diğer testler</div>
                          <div>• Null hipotez sınaması</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
                      <Calculator className="h-5 w-5 text-blue-400" />
                      İstatistiksel Anlamlılık
                    </h3>
                    <div className="space-y-3">
                      <p className="text-slate-300">
                        P-value, güven aralıkları ve effect size gibi
                        istatistiksel ölçütlerle sonuçları değerlendirir.
                      </p>
                      <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
                        <h4 className="font-semibold text-blue-200 mb-2">Ölçütler:</h4>
                        <div className="space-y-1 text-sm text-slate-300">
                          <div>• p &lt; 0.05: İstatistiksel anlamlılık</div>
                          <div>• p &lt; 0.01: Yüksek anlamlılık</div>
                          <div>• Effect size: Etkinin büyüklüğü</div>
                          <div>• Confidence interval: Güven aralığı</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Historical Development */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Tarihsel Gelişim</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-xl font-semibold text-slate-100 mb-4 text-amber-300">Michel Gauquelin (1928-1991)</h3>
                  <div className="space-y-3">
                    <p className="text-slate-300">
                      Fransız psikolog ve istatistikçi. İstatistiksel astrolojinin
                      kurucusu olarak kabul edilir. "Mars Etkisi" keşfiyle ünlü.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Mars Etkisi:</strong> Sporcuların doğum saatlerinde Mars anomalisi</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Sektörel Analiz:</strong> 36 sektör sistemini geliştirdi</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>25,000+ veri:</strong> Eminent kişiler üzerine araştırma</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-xl font-semibold text-slate-100 mb-4 text-green-300">Modern Dönem Gelişmeleri</h3>
                  <div className="space-y-3">
                    <p className="text-slate-300">
                      Suitbert Ertel, Geoffrey Dean, Ivan Kelly gibi araştırmacılar
                      tarafından meta-analizler ve replikasyon çalışmaları yapıldı.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Suitbert Ertel:</strong> Gauquelin etkisini doğruladı</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Geoffrey Dean:</strong> Twin Time ve meta-analiz çalışmaları</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Bilgisayar analizi:</strong> Modern istatistiksel yazılımlar</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Major Findings */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Başlıca Bulgular</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Mars Effect */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-700 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">♂</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Mars Etkisi</h3>
                  </div>
                  <div className="space-y-2">
                    <p className="text-slate-300 text-sm font-medium">İstatistiksel Anlamlılık: p &lt; 0.001</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-red-400 rounded-full"></div>
                        <span className="text-slate-400">Ünlü sporcularda Mars 1. ve 4. sektörlerde yoğun</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-red-400 rounded-full"></div>
                        <span className="text-slate-400">2,088 sporcunun analizinde doğrulandı</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-red-400 rounded-full"></div>
                        <span className="text-slate-400">Bağımsız araştırmalarla replike edildi</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Jupiter Effect */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">♃</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Jüpiter Etkisi</h3>
                  </div>
                  <div className="space-y-2">
                    <p className="text-slate-300 text-sm font-medium">İstatistiksel Anlamlılık: p &lt; 0.01</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                        <span className="text-slate-400">Aktörler ve politikacılarda Jüpiter yoğunluğu</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                        <span className="text-slate-400">3,647 ünlü kişinin analizinde tespit edildi</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                        <span className="text-slate-400">Özellikle kamu figürlerinde belirgin</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Saturn Effect */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-gray-500 to-slate-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">♄</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Satürn Etkisi</h3>
                  </div>
                  <div className="space-y-2">
                    <p className="text-slate-300 text-sm font-medium">İstatistiksel Anlamlılık: p &lt; 0.05</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                        <span className="text-slate-400">Bilim insanlarında Satürn yoğunluğu</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                        <span className="text-slate-400">3,142 bilim insanının analizinde bulundu</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                        <span className="text-slate-400">Özellikle matematik ve fizik alanında</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Moon Phases */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">☽</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Ay Fazları Etkisi</h3>
                  </div>
                  <div className="space-y-2">
                    <p className="text-slate-300 text-sm font-medium">İstatistiksel Anlamlılık: p &lt; 0.02</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-purple-400 rounded-full"></div>
                        <span className="text-slate-400">Doğum oranlarında Ay fazı korelasyonu</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-purple-400 rounded-full"></div>
                        <span className="text-slate-400">Tam Ay'da hafif artış gözlemlendi</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-purple-400 rounded-full"></div>
                        <span className="text-slate-400">5.9 milyon doğum kaydı analizi</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Seasonal Variations */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-teal-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">🍂</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Mevsimsel Değişimler</h3>
                  </div>
                  <div className="space-y-2">
                    <p className="text-slate-300 text-sm font-medium">İstatistiksel Anlamlılık: p &lt; 0.001</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                        <span className="text-slate-400">Belirli mesleklerde mevsimsel doğum yoğunluğu</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                        <span className="text-slate-400">Sporcular daha çok İlkbahar-Yaz doğumlu</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                        <span className="text-slate-400">Bilim insanları Sonbahar-Kış ağırlıklı</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Genetic Studies */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-rose-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">🧬</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Genetik Korelasyonlar</h3>
                  </div>
                  <div className="space-y-2">
                    <p className="text-slate-300 text-sm font-medium">İstatistiksel Anlamlılık: p &lt; 0.05</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-pink-400 rounded-full"></div>
                        <span className="text-slate-400">Aile üyeleri arasında gezegen pozisyon benzerlikleri</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-pink-400 rounded-full"></div>
                        <span className="text-slate-400">Parent-child Güneş ve Ay korelasyonları</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-pink-400 rounded-full"></div>
                        <span className="text-slate-400">30,000+ aile çifti analizinde tespit</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Research Methods */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Araştırma Yöntemleri</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                    <PieChart className="h-5 w-5 text-cyan-400" />
                    Sektörel Analiz
                  </h3>
                  <div className="space-y-3">
                    <p className="text-slate-300">
                      Gökyüzü 36 eşit sektöre bölünür. Her sektördeki
                      gezegen frekansları istatistiksel olarak analiz edilir.
                    </p>
                    <div className="bg-cyan-900/20 border border-cyan-700/50 rounded-lg p-4">
                      <h4 className="font-semibold text-cyan-200 mb-2">Önemli Sektörler:</h4>
                      <div className="space-y-1 text-sm text-slate-300">
                        <div>• Sektör 1-2: Doğuş noktası (ASC)</div>
                        <div>• Sektör 10-11: Zenit (MC)</div>
                        <div>• Sektör 19-20: Batış noktası (DSC)</div>
                        <div>• Sektör 28-29: Nadir (IC)</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-purple-400" />
                    Chi-Square Testi
                  </h3>
                  <div className="space-y-3">
                    <p className="text-slate-300">
                      Beklenen ve gözlemlenen frekanslar arasındaki
                      farkların istatistiksel anlamlılığını test eder.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-purple-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>H0:</strong> Rastgele dağılım hipotezi</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-purple-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>H1:</strong> Anlamlı korelasyon varlığı</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-purple-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>α = 0.05:</strong> Anlamlılık düzeyi</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Controversial Results */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Tartışmalı Sonuçlar</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-4 text-green-300">Destekleyen Bulgular</h3>
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></div>
                          <span className="text-sm text-slate-400"><strong>Gauquelin Etkisi:</strong> Birçok bağımsız çalışmada doğrulandı</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></div>
                          <span className="text-sm text-slate-400"><strong>Meta-analizler:</strong> Ertel ve Mürtz'ün kapsamlı incelemeleri</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></div>
                          <span className="text-sm text-slate-400"><strong>Replikasyon:</strong> Farklı kültürlerde benzer sonuçlar</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></div>
                          <span className="text-sm text-slate-400"><strong>Effect size:</strong> Küçük ama tutarlı etkiler</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-4 text-red-300">Eleştiriler ve Sınırlamalar</h3>
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-red-400 rounded-full mt-1.5 flex-shrink-0"></div>
                          <span className="text-sm text-slate-400"><strong>Data mining:</strong> Çoklu karşılaştırma problemi</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-red-400 rounded-full mt-1.5 flex-shrink-0"></div>
                          <span className="text-sm text-slate-400"><strong>Selection bias:</strong> Eminence kriterleri tartışmalı</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-red-400 rounded-full mt-1.5 flex-shrink-0"></div>
                          <span className="text-sm text-slate-400"><strong>Publication bias:</strong> Negatif sonuçlar yayınlanmama eğilimi</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-red-400 rounded-full mt-1.5 flex-shrink-0"></div>
                          <span className="text-sm text-slate-400"><strong>Küçük effect size:</strong> Pratik anlamlılık sorgulanıyor</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Modern Applications */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Modern Uygulamalar</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 text-blue-300">Big Data Analizleri</h3>
                  <div className="space-y-3">
                    <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-4">
                      <div className="font-medium text-blue-200 mb-2">Güncel Projeler:</div>
                      <div className="text-sm text-slate-300 space-y-1">
                        <div>• Milyonlarca doğum kaydının analizi</div>
                        <div>• Machine learning algoritmaları</div>
                        <div>• Sosyal medya veri madenciliği</div>
                        <div>• Longitudinal kohort çalışmaları</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 text-purple-300">Interdisipliner Yaklaşım</h3>
                  <div className="space-y-3">
                    <div className="bg-purple-900/20 border border-purple-700/30 rounded-lg p-4">
                      <div className="font-medium text-purple-200 mb-2">İşbirliği Alanları:</div>
                      <div className="text-sm text-slate-300 space-y-1">
                        <div>• Kronobiyoloji ve circadian rhythms</div>
                        <div>• Sosyal psikoloji ve kişilik</div>
                        <div>• Genetik ve epigenetik</div>
                        <div>• Nöroloji ve beyin görüntüleme</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Research Tools */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Araştırma Araçları</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-4 text-emerald-300">İstatistiksel Yazılımlar</h3>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-emerald-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>R Programming:</strong> En popüler istatistik yazılımı</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-emerald-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>SPSS:</strong> Sosyal bilimler için yaygın kullanım</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-emerald-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Python:</strong> Machine learning ve data science</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-emerald-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>SAS:</strong> Büyük veri analizi için</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-4 text-orange-300">Astroloji Veri Araçları</h3>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-orange-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Swiss Ephemeris:</strong> Yüksek hassasiyetli efemeris</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-orange-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Astrodienst:</strong> Online hesaplama araçları</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-orange-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Matrix Software:</strong> Profesyonel astroloji yazılımı</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-orange-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Özel API'lar:</strong> Veri toplanması için</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Study Resources */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Önerilen Kaynaklar
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-slate-200 mb-3">Temel Araştırmalar</h4>
                  <div className="space-y-2 text-sm text-slate-400">
                    <div>• "Neo-Astrology: A Copernican Revolution" - Michel Gauquelin</div>
                    <div>• "The Mars Effect" - Michel & Francoise Gauquelin</div>
                    <div>• "Is Astrology Relevant to Consciousness and Psi?" - Suitbert Ertel</div>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-200 mb-3">Meta-Analizler ve Eleştiriler</h4>
                  <div className="space-y-2 text-sm text-slate-400">
                    <div>• "Recent Advances in Natal Astrology" - Geoffrey Dean</div>
                    <div>• "The 'Mars Effect': French Test of Over 1,000 Sports Champions" - Ertel & Irving</div>
                    <div>• "Statistical Problems in ESP Research" - Jessica Utts</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Important Note */}
            <div className="bg-red-900/20 border border-red-700/50 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-red-200 mb-3">⚠️ Önemli Açıklama</h3>
              <p className="text-red-100 leading-relaxed">
                İstatistiksel astroloji, astrolojik iddiaları bilimsel yöntemlerle test eden ciddi bir araştırma alanıdır.
                Bu konudaki bulgular hala tartışmalıdır ve bilimsel consensus mevcut değildir.
                Bu bilgiler sadece eğitim amaçlıdır ve hiçbiri kesin bilimsel sonuç olarak sunulmamaktadır.
              </p>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center">
              <Link
                href="/about/astrology/advanced-systems/arabic-parts"
                className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Arap Parçaları
              </Link>

              <Link
                href="/about/astrology/advanced-systems/cosmobiology"
                className="inline-flex items-center px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors"
              >
                Kozmobiyoloji
                <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}